/* oxlint-disable jsx-a11y/prefer-tag-over-role, jsx-a11y/click-events-have-key-events */
// The ARIA combobox pattern: a list that a native select cannot draw (group headings, a tick on a
// chosen person, a "New" line). The keys are on the box (the arrows, Enter, Escape) with
// `aria-activedescendant`, as the pattern has it, so the options take clicks only.
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { searchByName, type NameSearchBox } from "@teamna/shared";
import { useTrackNameSearch } from "@teamna/web-client/hooks/use-name-search.ts";
import { controlClass, type ControlSize } from "./control.ts";

/**
 * A search box that suggests as you type, and picks with Enter or a click (WAI-ARIA combobox with a
 * list). For a long list of people where a select would be a scroll: the swimmers of a business in
 * step 2 of the WhatsApp sync tool.
 *
 * - The options come in the order the caller gives, under their `group` heading ("Same family
 *   name" first, then the rest). Typing keeps the options that the name search rule finds by
 *   `label` (or by `detail`, a little lower), best first under each heading, with the typed part in
 *   bold when the label holds it. At most `maxResults` show.
 * - An option already `added` stays in the list, green with a tick and "Added", so the owner sees
 *   who is chosen. It cannot be picked again: a click does nothing and the arrow keys step over it.
 * - With `onCreate`, the last line is always "New ...: (the words typed)", for a person who is not
 *   in the list yet.
 * - After a pick the words clear and the list stays open, so a second child is one more pick.
 * - The list is drawn over the page (a portal on `document.body`, under the box), so a table frame
 *   or a panel that clips its content never cuts it off.
 *
 * The box is the compact control of the input family (`control.ts`). Its label is hidden: the
 * column header or the row names it, and `label` is its accessible name.
 */

export type ComboboxOption = {
  id: string;
  label: string;
  /** A second line of small text: "7, Rana Noor's family". Searched too. */
  detail?: string;
  group?: string;
  added?: boolean;
};

type Item = { kind: "head"; text: string } | { kind: "option"; option: ComboboxOption } | { kind: "create"; text: string };

function bold(text: string, words: string): ReactNode {
  const index = words ? text.toLowerCase().indexOf(words.toLowerCase()) : -1;
  if (index < 0) return text;
  return (
    <>
      {text.slice(0, index)}
      <b className="font-bold text-ink">{text.slice(index, index + words.length)}</b>
      {text.slice(index + words.length)}
    </>
  );
}

const NEW_LINE = (words: string) => (words ? `New: ${words}` : "New");

export function Combobox({
  label,
  options,
  onPick,
  onCreate,
  createLabel = NEW_LINE,
  placeholder,
  size = "compact",
  maxResults = 8,
  className = "",
  searchBox,
}: {
  /** The accessible name: "Find a swimmer for Maryam Hashimi". */
  label: string;
  options: readonly ComboboxOption[];
  onPick: (option: ComboboxOption) => void;
  /** The last line of the list. Gets the words typed (maybe empty). */
  onCreate?: (words: string) => void;
  /** The words of the last line: `(words) => words ? \`New swimmer: ${words}\` : "New swimmer"`. */
  createLabel?: (words: string) => string;
  placeholder?: string;
  size?: ControlSize;
  maxResults?: number;
  className?: string;
  /** The search box, for the `name_search_used` event. */
  searchBox?: NameSearchBox;
}) {
  const id = useId();
  const listId = `${id}-list`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [words, setWords] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [place, setPlace] = useState<{ top: number; left: number; width: number } | null>(null);

  // Where the list goes: under the box, the box's width. Again on every scroll and resize while open.
  useEffect(() => {
    if (!open) return;
    const measure = () => {
      const box = inputRef.current?.getBoundingClientRect();
      if (box) setPlace({ top: box.bottom + 4, left: box.left, width: box.width });
    };
    measure();
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [open]);

  const found = useMemo(() => searchByName(options, words, (option) => [option.label, { text: option.detail, via: true }]), [options, words]);
  useTrackNameSearch(searchBox ?? null, words, found.results, Math.min(found.rows.length, maxResults));

  const items = useMemo<Item[]>(() => {
    // The headings keep the caller's order; inside a heading, the best hit comes first.
    const headings = new Map<string | undefined, number>();
    for (const option of options) if (!headings.has(option.group)) headings.set(option.group, headings.size);
    const hits = [...found.rows].sort((a, b) => headings.get(a.group)! - headings.get(b.group)!).slice(0, maxResults);
    const out: Item[] = [];
    let group: string | undefined;
    for (const option of hits) {
      if (option.group && option.group !== group) out.push({ kind: "head", text: option.group });
      group = option.group;
      out.push({ kind: "option", option });
    }
    if (onCreate) out.push({ kind: "create", text: words.trim() });
    return out;
  }, [found, options, words, maxResults, onCreate]);

  // The items a person can move to: options not added yet, and the "New" line.
  const choosable = items.flatMap((item, index) => (item.kind === "create" || (item.kind === "option" && !item.option.added) ? [index] : []));
  const current = choosable.includes(active) ? active : (choosable[0] ?? -1);
  const optionId = (index: number) => `${id}-option-${index}`;

  function choose(index: number) {
    const item = items[index];
    if (!item || item.kind === "head" || (item.kind === "option" && item.option.added)) return;
    if (item.kind === "create") {
      onCreate?.(item.text);
      setWords("");
      setOpen(false);
      return;
    }
    onPick(item.option);
    setWords("");
    setActive(0);
    inputRef.current?.focus();
  }

  function move(step: 1 | -1) {
    if (choosable.length === 0) return;
    const at = choosable.indexOf(current);
    const next = choosable[(at + step + choosable.length) % choosable.length]!;
    setActive(next);
    document.getElementById(optionId(next))?.scrollIntoView?.({ block: "nearest" });
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      move(event.key === "ArrowDown" ? 1 : -1);
    } else if (event.key === "Enter") {
      if (open && current >= 0) {
        event.preventDefault();
        choose(current);
      }
    } else if (event.key === "Escape") {
      if (open) {
        event.preventDefault();
        setOpen(false);
      }
    }
  }

  return (
    <div className={`relative min-w-0 ${className}`}>
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-label={label}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={open && current >= 0 ? optionId(current) : undefined}
        autoComplete="off"
        value={words}
        placeholder={placeholder}
        onChange={(event) => {
          setWords(event.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
        className={controlClass({ size, className: "px-3" })}
      />
      {open ? createPortal(
      <div
        id={listId}
        role="listbox"
        tabIndex={-1}
        aria-label={label}
        // A press in the list must not take the focus from the box (the blur would close it first).
        onMouseDown={(event) => event.preventDefault()}
        style={place ? { top: place.top, left: place.left, width: place.width } : undefined}
        // Above a panel (z-50) and under a dialog or a toast (z-60).
        className="fixed z-[55] max-h-72 overflow-y-auto rounded-xl border border-line-strong bg-paper p-1 shadow-card"
      >
        {items.map((item, index) => {
          if (item.kind === "head") {
            return (
              <div key={`head-${item.text}`} role="presentation" className="px-2 pt-2 pb-0.5 text-[11px] font-semibold tracking-[0.08em] text-ink-2 uppercase">
                {item.text}
              </div>
            );
          }
          const isActive = index === current;
          if (item.kind === "create") {
            return (
              <div
                key="create"
                id={optionId(index)}
                role="option"
                tabIndex={-1}
                aria-selected={isActive}
                onClick={() => choose(index)}
                className={[
                  "mt-1 cursor-pointer rounded-lg border-t border-line px-2 py-2 text-[14px] font-semibold text-accent-strong",
                  isActive ? "bg-accent-surface" : "hover:bg-surface",
                ].join(" ")}
              >
                {createLabel(item.text)}
              </div>
            );
          }
          const { option } = item;
          return (
            <div
              key={option.id}
              id={optionId(index)}
              role="option"
              tabIndex={-1}
              aria-selected={isActive}
              aria-disabled={option.added || undefined}
              onClick={() => choose(index)}
              className={[
                "flex items-baseline justify-between gap-3 rounded-lg px-2 py-1.5 text-[14px]",
                option.added
                  ? "cursor-default bg-confirmed-surface text-confirmed-ink"
                  : isActive
                    ? "cursor-pointer bg-accent-surface text-accent-strong"
                    : "cursor-pointer text-ink hover:bg-surface",
              ].join(" ")}
            >
              <span className="min-w-0" dir="auto">
                {option.added ? (
                  <svg aria-hidden="true" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" className="mr-1 inline size-3.5 align-[-2px]">
                    <path d="M4.5 10.5 8.5 14.5 15.5 6" />
                  </svg>
                ) : null}
                {bold(option.label, words.trim())}
                {option.detail ? <span className={option.added ? "text-confirmed-ink" : "text-ink-2"}>, {bold(option.detail, words.trim())}</span> : null}
              </span>
              {option.added ? <span className="shrink-0 text-[12px] font-bold">Added</span> : null}
            </div>
          );
        })}
      </div>,
      document.body,
    ) : null}
    </div>
  );
}
